import React from 'react';

// Process-wide caches so N reference cards (and the detail + edit views showing
// the same links) don't each hit the network. `resolved` holds settled titles
// (including null for "no title"); `inflight` de-dupes concurrent requests for
// the same URL so duplicate cards share one fetch.
const resolved = new Map<string, string | null>();
const inflight = new Map<string, Promise<string | null>>();

function oembedEndpoint(url: string, platform: string | null): string | null {
  if (platform === 'youtube') return `https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(url)}`;
  if (platform === 'vimeo') return `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`;
  return null;
}

/**
 * Resolves a reference video's real title from the provider's public oEmbed
 * endpoint. YouTube and Vimeo send CORS headers, so this works client-side with
 * no BE proxy.
 *
 * Returns null while loading and for providers with no usable oEmbed (Instagram
 * and TikTok now gate theirs behind a token) — callers fall back to a generic
 * label. Results are cached module-side, so a title fetched once is reused
 * everywhere it appears without re-fetching or re-flashing the fallback.
 */
export function useVideoTitle(url: string, platform: string | null): string | null {
  // Seed synchronously from cache so an already-known title never flickers.
  const [title, setTitle] = React.useState<string | null>(() => resolved.get(url) ?? null);
  React.useEffect(() => {
    if (resolved.has(url)) return; // already settled — initial state covered it
    const endpoint = oembedEndpoint(url, platform);
    if (!endpoint) return;
    let cancelled = false;
    let promise = inflight.get(url);
    if (!promise) {
      promise = fetch(endpoint)
        .then((r) => (r.ok ? r.json() : null))
        .then((d: { title?: string } | null) => d?.title ?? null)
        .catch(() => null)
        .then((t) => {
          resolved.set(url, t);
          inflight.delete(url);
          return t;
        });
      inflight.set(url, promise);
    }
    promise.then((t) => {
      if (!cancelled) setTitle(t);
    });
    return () => {
      cancelled = true;
    };
  }, [url, platform]);
  return title;
}
